// Pure format builders for the EOD report. String in, string out — no DOM, no
// React, no network. All three outputs render from the same payload the screen
// uses, so a printed report can never disagree with what was on screen.
//
// Section titles here are English literals rather than t() calls: they are
// column names for spreadsheet and print consumers, and must stay stable
// regardless of the viewer's language. The on-screen headings ARE translated —
// see EodSalesSections.js.
import { escapeField as csvCell } from '../../../../components/bulkImport/csvUtils';

const money = (value, currency) =>
    `${currency || ''}${Number(value || 0).toFixed(2)}`;

// Product names are tenant-controlled input rendered into a document, so every
// interpolated value goes through this.
const esc = (value) =>
    String(value === null || value === undefined ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

// ─── Section model — one description drives all three formats ────────────────
// Each section names its columns once. CSV writes them as a header row, the two
// HTML builders write them as <th>. Adding a section means adding one entry.
// `nested` controls how add-ons are carried, because the two output kinds want
// genuinely different shapes:
//
//   nested: true  (screen, A4, receipt) — documents people READ. Add-ons are
//     indented under the product they were sold with, matching the Sold table.
//   nested: false (CSV) — a file people PIVOT. An indented row would be
//     indistinguishable from a product once the indent is stripped, so add-ons
//     get their own section with an explicit Product column instead.
const sections = (report, { nested = true } = {}) => {
    const c = report.currency;
    const ing = report.ingredients || {};
    const products = report.products || [];
    const list = [
        {
            title: 'Payment Methods',
            columns: ['Method', 'Count', 'Total'],
            rows: (report.payment_methods || []).map(r =>
                [r.method, r.count, money(r.total, c)]),
        },
        {
            title: 'Dining Options',
            columns: ['Option', 'Count', 'Total'],
            rows: (report.dining_options || []).map(r =>
                [r.option, r.count, money(r.total, c)]),
        },
        {
            title: 'Categories',
            columns: ['Category', 'Quantity', 'Revenue'],
            rows: (report.categories || []).map(r =>
                [r.category, r.quantity, money(r.revenue, c)]),
        },
        {
            title: 'Products',
            columns: ['Product', 'Category', 'Quantity', 'Revenue'],
            rows: products.flatMap(r => {
                const productRow = [r.product_name, r.category, r.quantity, money(r.revenue, c)];
                if (!nested) return [productRow];
                // Category is left blank on an add-on row: it inherits the
                // product's, and repeating it reads like a second product.
                return [
                    productRow,
                    ...(r.addons || []).map(a => ({
                        cells: [a.name, '', a.quantity, money(a.revenue, c)],
                        indent: true,
                    })),
                ];
            }),
        },
        {
            title: 'Modifiers',
            columns: ['Modifier', 'Quantity', 'Revenue'],
            rows: (report.modifiers || []).map(r =>
                [r.name, r.quantity, money(r.revenue, c)]),
        },
        {
            title: 'Expenses',
            columns: ['Category', 'Item', 'Total'],
            rows: (report.expenses || []).map(r =>
                [r.category, r.product, money(r.total, c)]),
        },
    ];

    // CSV only: the add-on/product relationship the indent carries visually,
    // expressed as a real column so it survives into a spreadsheet.
    if (!nested) {
        list.splice(4, 0, {
            title: 'Add-ons by Product',
            columns: ['Product', 'Add-on', 'Quantity', 'Revenue'],
            rows: products.flatMap(p =>
                (p.addons || []).map(a =>
                    [p.product_name, a.name, a.quantity, money(a.revenue, c)])),
        });
    }

    // Add-ons the POS recorded with no parent product — they sold, so they are
    // reported, just not under anything.
    list.push({
        title: 'Unattached Add-ons',
        columns: ['Add-on', 'Quantity', 'Revenue'],
        rows: (report.unattached_modifiers || []).map(r =>
            [r.name, r.quantity, money(r.revenue, c)]),
    });

    // Waste and adjustments stay in their own sections — never folded into
    // usage, so "90g beans used" cannot silently mean "80g used + 10g binned".
    [
        ['usage', 'Ingredients Used'],
        ['waste', 'Ingredients Wasted'],
        ['adjustments', 'Ingredient Adjustments'],
        ['purchases', 'Ingredients Purchased'],
    ].forEach(([key, title]) => {
        list.push({
            title,
            columns: ['Ingredient', 'Quantity', 'Unit'],
            rows: (ing[key] || []).map(r => [r.name, r.quantity, r.unit]),
        });
    });

    return list.filter(s => s.rows.length > 0);
};

// A section row is either a plain array of cells, or {cells, indent} when it
// should render indented under the row above.
const rowCells = (row) => (Array.isArray(row) ? row : row.cells);
const rowIndent = (row) => (Array.isArray(row) ? false : Boolean(row.indent));

const summaryRows = (report) => {
    const c = report.currency;
    const totals = report.totals || {};
    const cash = report.cash || {};
    const rows = [
        ['Transactions', totals.transactions],
        ['Gross Sales', money(totals.gross_sales, c)],
        ['Discounts', money(totals.discounts, c)],
        ['Promotions', money(totals.promo_discounts, c)],
        ['Expenses', money(totals.expenses, c)],
        ['Till Total', money(totals.till_total, c)],
    ];
    if (cash.sessions) {
        rows.push(
            ['Start Cash', money(cash.start_cash, c)],
            ['Expected Cash', money(cash.expected_cash, c)],
            ['Counted Cash', money(cash.counted_cash, c)],
            ['Cash Difference', money(cash.difference, c)],
        );
    }
    return rows;
};

// ─── CSV ─────────────────────────────────────────────────────────────────────
const buildEodCsv = (report) => {
    const lines = [
        `EOD Report,${csvCell(report.sales_date)}`,
        `Generated,${csvCell(report.generated_at)}`,
    ];
    if (report.brand_name) lines.push(`Business,${csvCell(report.brand_name)}`);

    lines.push('', 'Summary', 'Metric,Value');
    summaryRows(report).forEach(([label, value]) => {
        lines.push(`${csvCell(label)},${csvCell(value)}`);
    });

    sections(report, { nested: false }).forEach(section => {
        lines.push('', section.title, section.columns.map(csvCell).join(','));
        section.rows.forEach(row => lines.push(rowCells(row).map(csvCell).join(',')));
    });

    return lines.join('\n');
};

// ─── A4 / PDF ────────────────────────────────────────────────────────────────
const A4_STYLES = `
body { font-family: 'Plus Jakarta Sans', Arial, sans-serif; color: #212529; margin: 24px; }
h1 { font-size: 20px; margin: 0; }
h2 { font-size: 14px; margin: 24px 0 8px; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
.meta { color: #64748b; font-size: 12px; margin-top: 4px; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
th { background: #f8fafc; font-weight: 700; }
td:last-child, th:last-child { text-align: right; }
section { page-break-inside: avoid; }
tr.sub td:first-child { padding-left: 26px; color: #64748b; }
@media print { body { margin: 0; } }
`;

const htmlTable = (section) => `
<section>
  <h2>${esc(section.title)}</h2>
  <table>
    <thead><tr>${section.columns.map(col => `<th>${esc(col)}</th>`).join('')}</tr></thead>
    <tbody>${section.rows.map(row => {
        const cells = rowCells(row);
        const cls = rowIndent(row) ? ' class="sub"' : '';
        return `<tr${cls}>${cells.map(cell => `<td>${esc(cell)}</td>`).join('')}</tr>`;
    }).join('')}</tbody>
  </table>
</section>`;

const buildEodA4Html = (report, t) => `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>${esc(t('end_of_day_report'))} ${esc(report.sales_date)}</title>
<style>${A4_STYLES}</style></head>
<body>
  <h1>${esc(report.brand_name || t('end_of_day_report'))}</h1>
  <div class="meta">${esc(t('end_of_day_report'))} &middot; ${esc(report.sales_date)}</div>
  <div class="meta">${esc(t('report_generated'))}: ${esc(report.generated_at)}</div>
  ${htmlTable({
      title: t('summary'),
      columns: [t('metric'), t('value')],
      rows: summaryRows(report),
  })}
  ${sections(report).map(htmlTable).join('')}
</body>
</html>`;

// ─── 80mm roll ───────────────────────────────────────────────────────────────
// The local-print fallback. When the tenant is on a network Epson printer the
// report renders server-side instead, via receipt_renderer.render_eod_report.
const RECEIPT_STYLES = `
@page { size: 80mm auto; margin: 0; }
body { width: 80mm; font-family: 'Courier New', monospace; font-size: 11px; margin: 0; padding: 4mm; color: #000; }
h1 { font-size: 13px; text-align: center; margin: 0 0 2mm; }
h2 { font-size: 11px; margin: 4mm 0 1mm; border-bottom: 1px dashed #000; padding-bottom: 1mm; }
.meta { text-align: center; font-size: 10px; margin-bottom: 2mm; }
table { width: 100%; border-collapse: collapse; font-size: 10px; }
td { padding: 0.5mm 0; vertical-align: top; }
td:last-child { text-align: right; white-space: nowrap; }
@media print { body { padding: 0 2mm; } }
`;

// A roll is too narrow for real columns, so each row collapses to a label and
// its last value — the number the reader is actually looking for.
const receiptTable = (section) => `
<h2>${esc(section.title)}</h2>
<table><tbody>${section.rows.map(row => {
    const cells = rowCells(row).filter(v => v !== '');
    const last = cells[cells.length - 1];
    const rest = cells.slice(0, -1).join(' · ');
    // A roll has no room for real indentation; a marker carries it instead.
    const label = rowIndent(row) ? `  + ${rest}` : rest;
    return `<tr><td>${esc(label)}</td><td>${esc(last)}</td></tr>`;
}).join('')}</tbody></table>`;

const buildEodReceiptHtml = (report, t) => `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>${esc(t('end_of_day_report'))} ${esc(report.sales_date)}</title>
<style>${RECEIPT_STYLES}</style></head>
<body>
  <h1>${esc(report.brand_name || t('end_of_day_report'))}</h1>
  <div class="meta">${esc(t('end_of_day_report'))}<br/>${esc(report.sales_date)}</div>
  ${receiptTable({ title: t('summary'), rows: summaryRows(report) })}
  ${sections(report).map(receiptTable).join('')}
</body>
</html>`;

const eodFilename = (report, ext) => `eod-${report.sales_date}.${ext}`;

export { buildEodCsv, buildEodA4Html, buildEodReceiptHtml, eodFilename };
